/* ==========================================================
   Maintrade Header  –  assets/mt-header.js
   - Transparent over banner at top of page
   - Background added after scrolling
   - Hides on scroll down, reappears on scroll up
   - Dropdowns, predictive search, mobile drawer
   Re-initialises automatically in the theme editor.
   ========================================================== */
(function () {
  if (customElements.get('mt-header')) return;

  class MtHeader extends HTMLElement {
    connectedCallback() {
      this.ac = new AbortController();
      const signal = this.ac.signal;

      this.sticky = this.dataset.sticky === 'true';
      this.hideOnScroll = this.dataset.hideOnScroll === 'true';
      this.threshold = parseInt(this.dataset.threshold, 10) || 0;
      this.transparent = this.classList.contains('mt-header--transparent');
      this.searchUrl = this.dataset.searchUrl || '/search';
      this.labelAll = this.dataset.labelAll || 'Search';

      this.drawer = this.querySelector('[data-mt-drawer]');
      this.searchPanel = this.querySelector('[data-mt-search]');
      this.searchInput = this.querySelector('[data-mt-search-input]');
      this.searchResults = this.querySelector('[data-mt-search-results]');
      this.items = Array.from(this.querySelectorAll('[data-mt-item].has-dropdown'));

      this.lastY = Math.max(window.scrollY, 0);
      this.hovered = false;
      this.focused = false;
      this.ticking = false;
      this.closeTimers = new WeakMap();

      // ---- scroll
      window.addEventListener('scroll', () => {
        if (this.ticking) return;
        this.ticking = true;
        requestAnimationFrame(() => { this.onScroll(); this.ticking = false; });
      }, { passive: true, signal });

      // ---- hover / focus on header (for transparent state)
      this.addEventListener('pointerenter', (e) => { if (e.pointerType === 'mouse') { this.hovered = true; this.refresh(); } }, { signal });
      this.addEventListener('pointerleave', (e) => { if (e.pointerType === 'mouse') { this.hovered = false; this.refresh(); } }, { signal });
      this.addEventListener('focusin', (e) => { this.focused = e.target.matches(':focus-visible'); this.refresh(); }, { signal });
      this.addEventListener('focusout', () => { this.focused = false; this.refresh(); }, { signal });

      // ---- clicks (delegated)
      this.addEventListener('click', (e) => {
        const t = e.target;
        const toggle = t.closest('[data-mt-toggle]');
        if (toggle) {
          const item = toggle.closest('[data-mt-item]');
          const willOpen = !item.classList.contains('is-open');
          this.closeItems();
          if (willOpen) this.openItem(item);
          this.refresh();
          return;
        }
        if (t.closest('[data-mt-search-open]')) { this.toggleSearch(); return; }
        if (t.closest('[data-mt-search-close]')) { this.closeSearch(); return; }
        if (t.closest('[data-mt-menu-open]')) { this.openDrawer(); return; }
        if (t.closest('[data-mt-menu-close]')) { this.closeDrawer(); return; }
      }, { signal });

      // ---- desktop dropdown hover
      this.items.forEach((item) => {
        item.addEventListener('pointerenter', (e) => {
          if (e.pointerType !== 'mouse') return;
          clearTimeout(this.closeTimers.get(item));
          this.closeItems(item);
          this.openItem(item);
          this.refresh();
        }, { signal });
        item.addEventListener('pointerleave', (e) => {
          if (e.pointerType !== 'mouse') return;
          this.closeTimers.set(item, setTimeout(() => { this.closeItem(item); this.refresh(); }, 140));
        }, { signal });
      });

      // ---- click outside / Escape
      document.addEventListener('click', (e) => {
        if (!this.contains(e.target)) { this.closeItems(); this.closeSearch(); this.refresh(); }
      }, { signal });
      document.addEventListener('keydown', (e) => {
        if (e.key !== 'Escape') return;
        this.closeItems(); this.closeSearch(); this.closeDrawer();
      }, { signal });

      // ---- close drawer when resizing to desktop
      window.addEventListener('resize', () => {
        if (window.innerWidth >= 990) this.closeDrawer();
      }, { signal });

      // ---- predictive search
      if (this.searchInput) {
        let timer;
        this.searchInput.addEventListener('input', () => {
          clearTimeout(timer);
          const q = this.searchInput.value.trim();
          if (q.length < 2) { this.searchResults.innerHTML = ''; return; }
          timer = setTimeout(() => this.suggest(q), 250);
        }, { signal });
      }

      this.onScroll();
    }

    disconnectedCallback() {
      this.ac && this.ac.abort();
      document.body.classList.remove('mt-no-scroll');
    }

    /* ---------------- state ---------------- */
    isOpen() {
      return (
        this.classList.contains('is-search-open') ||
        (this.drawer && this.drawer.classList.contains('is-open')) ||
        this.items.some((i) => i.classList.contains('is-open'))
      );
    }

    refresh() {
      const y = Math.max(window.scrollY, 0);
      const top = y <= this.threshold;
      const open = this.isOpen();
      this.classList.toggle('is-top', top);
      this.classList.toggle('is-scrolled', !top);
      this.classList.toggle('is-open', open);
      this.classList.toggle('is-clear', this.transparent && top && !open && !this.hovered && !this.focused);
    }

    onScroll() {
      const y = Math.max(window.scrollY, 0);

      if (this.sticky && this.hideOnScroll) {
        const delta = y - this.lastY;
        if (y <= 0 || delta < -4 || this.isOpen() || this.focused) {
          this.classList.remove('is-hidden');
        } else if (delta > 4 && y > this.offsetHeight + 60) {
          this.classList.add('is-hidden');
        }
        if (Math.abs(delta) > 4 || y <= 0) this.lastY = y;
      }

      this.refresh();
    }

    /* ---------------- dropdowns ---------------- */
    openItem(item) {
      item.classList.add('is-open');
      const btn = item.querySelector('[data-mt-toggle]');
      if (btn) btn.setAttribute('aria-expanded', 'true');
    }
    closeItem(item) {
      item.classList.remove('is-open');
      const btn = item.querySelector('[data-mt-toggle]');
      if (btn) btn.setAttribute('aria-expanded', 'false');
    }
    closeItems(except) {
      this.items.forEach((i) => { if (i !== except) this.closeItem(i); });
    }

    /* ---------------- search ---------------- */
    toggleSearch() {
      this.classList.contains('is-search-open') ? this.closeSearch() : this.openSearch();
    }
    openSearch() {
      if (!this.searchPanel) return;
      this.closeItems();
      this.classList.remove('is-hidden');
      this.classList.add('is-search-open');
      this.querySelectorAll('[data-mt-search-open]').forEach((b) => b.setAttribute('aria-expanded', 'true'));
      this.refresh();
      setTimeout(() => this.searchInput && this.searchInput.focus(), 30);
    }
    closeSearch() {
      if (!this.classList.contains('is-search-open')) return;
      this.classList.remove('is-search-open');
      this.querySelectorAll('[data-mt-search-open]').forEach((b) => b.setAttribute('aria-expanded', 'false'));
      if (this.searchResults) this.searchResults.innerHTML = '';
      this.refresh();
    }

    async suggest(q) {
      if (this.abort) this.abort.abort();
      this.abort = new AbortController();
      const url = `${this.searchUrl}/suggest.json?q=${encodeURIComponent(q)}&resources[type]=product&resources[limit]=4&resources[options][unavailable_products]=last`;
      try {
        const res = await fetch(url, { signal: this.abort.signal });
        if (!res.ok) throw new Error('bad response');
        const json = await res.json();
        const products = (json.resources && json.resources.results && json.resources.results.products) || [];
        this.renderResults(q, products);
      } catch (err) {
        if (err.name !== 'AbortError') this.searchResults.innerHTML = '';
      }
    }

    renderResults(q, products) {
      const box = this.searchResults;
      box.innerHTML = '';

      if (products.length) {
        const grid = document.createElement('div');
        grid.className = 'mt-search__grid';
        products.forEach((p) => {
          const a = document.createElement('a');
          a.className = 'mt-search__item';
          a.href = p.url;
          const src = (p.featured_image && p.featured_image.url) || p.image;
          if (src) {
            const img = document.createElement('img');
            img.src = src + (src.includes('?') ? '&' : '?') + 'width=240';
            img.alt = (p.featured_image && p.featured_image.alt) || p.title;
            img.loading = 'lazy';
            a.appendChild(img);
          }
          const span = document.createElement('span');
          span.textContent = p.title;
          a.appendChild(span);
          grid.appendChild(a);
        });
        box.appendChild(grid);
      }

      const all = document.createElement('a');
      all.className = 'mt-search__all';
      all.href = `${this.searchUrl}?q=${encodeURIComponent(q)}&options[prefix]=last`;
      all.textContent = `${this.labelAll}: “${q}” →`;
      box.appendChild(all);
    }

    /* ---------------- mobile drawer ---------------- */
    openDrawer() {
      if (!this.drawer) return;
      this.closeSearch();
      this.drawer.classList.add('is-open');
      this.drawer.setAttribute('aria-hidden', 'false');
      this.querySelectorAll('[data-mt-menu-open]').forEach((b) => b.setAttribute('aria-expanded', 'true'));
      document.body.classList.add('mt-no-scroll');
      this.classList.remove('is-hidden');
      this.refresh();
    }
    closeDrawer() {
      if (!this.drawer || !this.drawer.classList.contains('is-open')) return;
      this.drawer.classList.remove('is-open');
      this.drawer.setAttribute('aria-hidden', 'true');
      this.querySelectorAll('[data-mt-menu-open]').forEach((b) => b.setAttribute('aria-expanded', 'false'));
      document.body.classList.remove('mt-no-scroll');
      this.refresh();
    }
  }

  customElements.define('mt-header', MtHeader);
})();